// src/globalService/api.service.ts

import axios from "axios";
import { User, Challenge, WayPoint, Brands, PortalParticipant } from "./api.model";

export const base_url =
  "https://virtualchallenge-platform-3edceab55a5f.herokuapp.com";

export const assets_base_url = "https://medalmad.com";

const getUrl = (endPoint: string): string => {
  const brandId = localStorage.getItem("brandId");
  return `${base_url}/${brandId}/${endPoint}`;
};

export const getBrands = async (): Promise<Brands[] | undefined> => {
  try {
    const response = await axios.get(`${base_url}/brands`);
    return response.data;
  } catch (error: any) {
    console.error("getBrands error", error);
    return undefined;
  }
};

export const getChallengesForMap = async (
  userId: string | null = null
): Promise<Challenge[] | undefined> => {
  try {
    const response = await axios.get(getUrl(`${userId}/products`));
    return response.data;
  } catch (error: any) {
    console.error("getChallengesForMap error", error);
    return undefined;
  }
};

export const getUsersByProductId = async (
  productId: string,
  userId: string
): Promise<User[] | undefined> => {
  try {
    const response = await axios.get(
      `${getUrl("users")}/${productId}/${userId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getUsersByProductId error", error);
    return undefined;
  }
};

export const getPortalParticipantsByProductId = async (
  productId: string,
  brandId?: string | null
): Promise<PortalParticipant[] | undefined> => {
  const bid = brandId ?? localStorage.getItem("brandId");

  const candidates: string[] = [];

  if (bid) {
    candidates.push(`${base_url}/${bid}/portal/${productId}/participants`);
    candidates.push(`${base_url}/${bid}/participants/${productId}`);
    candidates.push(`${base_url}/${bid}/users/${productId}`);
  }

  candidates.push(`${base_url}/portal/${productId}/participants`);
  candidates.push(`${base_url}/medalmad/portal/${productId}/participants`);

  for (const url of candidates) {
    try {
      const res = await axios.get(url);
      if (Array.isArray(res.data)) return res.data;
    } catch {
      // Try next endpoint
    }
  }

  try {
    const res = await axios.get(`${getUrl("users")}/${productId}/0`);
    if (Array.isArray(res.data)) return res.data;
  } catch {
    // Ignore
  }

  return undefined;
};

export const getChallengeById = async (
  productId: string
): Promise<Challenge[] | undefined> => {
  try {
    const response = await axios.get(
      `${getUrl("challengeById")}/${productId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getChallengeById error", error);
    return undefined;
  }
};

export const getProductById = async (
  productId: string
): Promise<Challenge | undefined> => {
  try {
    const response = await axios.get(
      `${base_url}/medalmad/product/${productId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getProductById error", error);
    return undefined;
  }
};

export const getWaypointsById = async (
  productId: string,
  userId: string
): Promise<WayPoint[] | undefined> => {
  try {
    const response = await axios.get(
      `${base_url}/medalmad/waypoints/${productId}/${userId}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getWaypointsById error", error);
    return undefined;
  }
};

export const unlockWaypoint = async (
  waypointId: string,
  userId: string,
  thingsToUpdate: { answer?: number }
): Promise<WayPoint[] | undefined> => {
  try {
    const response = await axios.put(
      `${getUrl(`waypoints/unlock/${waypointId}/${userId}`)}`,
      thingsToUpdate
    );
    return response.data;
  } catch (error: any) {
    console.error("unlockWaypoint error", error);
    return undefined;
  }
};

export const submitEvidence = async (
  productId: string,
  userId: string,
  thingsToUpdate: { distanceKm: number; durationSec: number }
) => {
  try {
    const response = await axios.post(
      `${getUrl(`evidence/${productId}/${userId}`)}`,
      thingsToUpdate
    );
    return response.data;
  } catch (error: any) {
    console.error("submitEvidence error", error);
    return undefined;
  }
};

export const getUserActivity = async (
  productId: string,
  userId: string
) => {
  try {
    const response = await axios.get(
      `${getUrl(`useractivity/${productId}/${userId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getUserActivity error", error);
    return undefined;
  }
};

export const getUserEvidences = async (
  productId: string,
  userId: string
) => {
  try {
    const response = await axios.get(
      `${getUrl(`evidence/${productId}/${userId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getUserEvidences error", error);
    return undefined;
  }
};

export const deleteUserEvidence = async (evidenceId: string) => {
  try {
    const response = await axios.delete(
      `${getUrl(`evidence/${evidenceId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("deleteUserEvidence error", error);
    return undefined;
  }
};

export const getStravaActivity = async (userId: string) => {
  try {
    const response = await axios.get(
      `${getUrl(`strava/activities/${userId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("getStravaActivity error", error);
    return undefined;
  }
};

export const disconnectStrava = async (userId: string) => {
  try {
    const response = await axios.post(
      `${getUrl(`strava/logout/${userId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("disconnectStrava error", error);
    return undefined;
  }
};

export const checkChallengeValidity = async (
  productId: string,
  userId: string
) => {
  try {
    const response = await axios.post(
      `${getUrl(`challenge-validity/${productId}/${userId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("checkChallengeValidity error", error);
    return undefined;
  }
};

export const downloadWaypointImage = async (
  waypointId: string,
  userId: string
) => {
  try {
    const response = await axios.post(
      `${getUrl(`image-download/${waypointId}/${userId}`)}`
    );
    return response.data;
  } catch (error: any) {
    console.error("downloadWaypointImage error", error);
    return undefined;
  }
};
